import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  fetchSprintById,
  fetchSprintBoard,
  updateSprint,
  startSprint,
  completeSprint,
  pauseSprint,
  resumeSprint,
  addItemToSprint,
  SprintStatus,
} from '../../../store/sprintSlice';
import api from '../../../api/axios';
import IncompleteItemsModal from './IncompleteItemsModal';
import AmendmentLog from '../../Common/AmendmentLog';
import BreachHistory from '../../Common/BreachHistory';
import { DndContext, closestCorners, DragEndEvent, DragStartEvent, DragOverlay, useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const statusColors: Record<SprintStatus, string> = {
  planned: 'bg-gray-100 text-gray-700 border-gray-300',
  active: 'bg-green-50 text-green-700 border-green-300',
  paused: 'bg-yellow-50 text-yellow-700 border-yellow-300',
  completed: 'bg-blue-50 text-blue-700 border-blue-300',
  cancelled: 'bg-red-50 text-red-700 border-red-300',
};

const COLUMNS = ['todo', 'in_progress', 'blocked', 'review', 'done'] as const;
const COLUMN_LABELS: Record<string, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  review: 'Review',
  done: 'Done',
};
const COLUMN_COLORS: Record<string, string> = {
  todo: 'bg-gray-50 border-gray-200',
  in_progress: 'bg-blue-50 border-blue-200',
  blocked: 'bg-red-50 border-red-200',
  review: 'bg-yellow-50 border-yellow-200',
  done: 'bg-green-50 border-green-200',
};

const SortableTaskCard: React.FC<{ task: any; onClick: () => void }> = ({ task, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task._id,
    data: { type: 'task', status: task.status },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
      className="bg-white rounded border border-gray-200 p-3 shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing"
      onClick={onClick}
    >
      <div className="flex items-center gap-1.5 mb-1">
        {task._itemType === 'bug' && (
          <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">bug</span>
        )}
        <p className="text-sm font-medium text-gray-900 line-clamp-2" title={task.title}>{task.title}</p>
      </div>
      {task.priority && (
        <span className={`mt-1 inline-flex items-center text-xs px-1.5 py-0.5 rounded ${
          task.priority === 'critical' ? 'bg-red-100 text-red-700' :
          task.priority === 'high' ? 'bg-orange-100 text-orange-700' :
          task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
          'bg-gray-100 text-gray-600'
        }`}>
          {task.priority}
        </span>
      )}
      {task.storyPoints > 0 && (
        <span className="ml-1 text-xs text-blue-600 font-medium">{task.storyPoints} SP</span>
      )}
    </div>
  );
};

const DroppableColumn: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`p-2 space-y-2 flex-1 min-h-[200px] transition-colors ${isOver ? 'bg-blue-50' : ''}`}>
      {children}
    </div>
  );
};

interface BacklogItem {
  _id: string;
  title: string;
  name?: string;
  status: string;
  priority?: string;
  type: 'task' | 'bug';
}

interface Props {
  sprintIdProp?: string;
  projectId?: string;
  onBack?: () => void;
}

const SprintDetail: React.FC<Props> = ({ sprintIdProp, projectId: projectIdProp, onBack }) => {
  const params = useParams<{ projectId: string; sprintId: string }>();
  const sprintId = sprintIdProp ?? params.sprintId;
  const projectId = projectIdProp ?? params.projectId;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentSprint, board, features, loading, error } = useAppSelector(s => s.sprints);
  const [tab, setTab] = useState<'board' | 'details' | 'add'>('board');
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [backlogItems, setBacklogItems] = useState<BacklogItem[]>([]);
  const [backlogLoading, setBacklogLoading] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [addSearch, setAddSearch] = useState('');
  const [addType, setAddType] = useState<'task' | 'bug' | 'all'>('all');
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [showAddRole, setShowAddRole] = useState(false);
  const [newRoleUserId, setNewRoleUserId] = useState('');
  const [newRoleUserName, setNewRoleUserName] = useState('');
  const [newRoleType, setNewRoleType] = useState<'editor' | 'viewer'>('editor');
  const incompleteItems = useAppSelector(s => s.sprints.incompleteItems);
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);

  useEffect(() => {
    if (sprintId) {
      dispatch(fetchSprintBoard({ id: sprintId }));
    }
  }, [dispatch, sprintId]);

  const [parentMap, setParentMap] = useState<Record<string, { name: string; type: string }>>({});
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (tab !== 'add' || !projectId) return;
    setBacklogLoading(true);
    Promise.all([
      api.get('/tasks', { params: { projectId, sprintId: 'none', limit: 200 } }),
      api.get('/bugs', { params: { projectId, sprintId: 'none', limit: 200 } }),
      api.get(`/features/project/${projectId}`),
      api.get(`/folders/project/${projectId}`),
    ]).then(([tasksRes, bugsRes, featuresRes, foldersRes]) => {
      const tasks: BacklogItem[] = (tasksRes.data.data ?? []).map((t: any) => ({ ...t, type: 'task' as const }));
      const bugs: BacklogItem[] = (bugsRes.data.data ?? []).map((b: any) => ({ ...b, title: b.title, type: 'bug' as const }));
      setBacklogItems([...tasks, ...bugs]);
      // Build parent name lookup
      const map: Record<string, { name: string; type: string }> = {};
      for (const f of (featuresRes.data.data ?? [])) map[f._id] = { name: f.name, type: 'feature' };
      for (const f of (foldersRes.data.data ?? [])) map[f._id] = { name: f.name, type: 'folder' };
      setParentMap(map);
    }).finally(() => setBacklogLoading(false));
  }, [tab, projectId]);

  const handleAddItem = async (item: BacklogItem) => {
    if (!sprintId) return;
    setAddingId(item._id);
    await dispatch(addItemToSprint({ id: sprintId, itemId: item._id, itemType: item.type }));
    setBacklogItems(prev => prev.filter(i => i._id !== item._id));
    setAddingId(null);
    dispatch(fetchSprintBoard({ id: sprintId }));
  };

  const handleLifecycle = async (action: string) => {
    if (!sprintId) return;
    switch (action) {
      case 'start': dispatch(startSprint(sprintId)); break;
      case 'complete': {
        const result = await dispatch(completeSprint(sprintId));
        if (completeSprint.fulfilled.match(result) && (result.payload.incompleteItems?.length ?? 0) > 0) {
          setShowIncompleteModal(true);
        }
        break;
      }
      case 'pause': dispatch(pauseSprint({ id: sprintId })); break;
      case 'resume': dispatch(resumeSprint({ id: sprintId })); break;
    }
  };

  const handleAddRole = async () => {
    if (!sprintId || !newRoleUserId.trim() || !newRoleUserName.trim()) return;
    try {
      await api.post(`/sprints/${sprintId}/roles`, { userId: newRoleUserId, userName: newRoleUserName, role: newRoleType });
      dispatch(fetchSprintBoard({ id: sprintId }));
      setNewRoleUserId('');
      setNewRoleUserName('');
      setShowAddRole(false);
    } catch { /* ignore */ }
  };

  const handleRemoveRole = async (userId: string) => {
    if (!sprintId) return;
    try {
      await api.delete(`/sprints/${sprintId}/roles/${userId}`);
      dispatch(fetchSprintBoard({ id: sprintId }));
    } catch { /* ignore */ }
  };

  const handleNameSave = async () => {
    if (!sprintId || !nameInput.trim()) return;
    await dispatch(updateSprint({ id: sprintId, data: { name: nameInput } }));
    setEditingName(false);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveTaskId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveTaskId(null);
    const { active, over } = event;
    if (!over || !sprintId) return;

    const taskId = active.id as string;
    const newStatus = over.id as string;

    // Find the task's current column
    const allTasks = Object.entries(board || {}).flatMap(([col, tasks]) =>
      (tasks as any[]).map(t => ({ ...t, _col: col }))
    );
    const task = allTasks.find(t => t._id === taskId);
    if (!task || task._col === newStatus) return;

    try {
      if (task._itemType === 'bug') {
        // Map kanban column back to bug status
        const bugStatusMap: Record<string, string> = {
          todo: 'reported', in_progress: 'in_progress', blocked: 'in_progress', review: 'testing', done: 'fixed',
        };
        await api.put(`/bugs/${taskId}/status`, { status: bugStatusMap[newStatus] || newStatus });
      } else {
        await api.put(`/tasks/${taskId}/status`, { status: newStatus });
      }
      dispatch(fetchSprintBoard({ id: sprintId }));
    } catch { /* revert handled by refetch */ }
  };

  if (loading && !currentSprint) {
    return <div className="flex justify-center p-12"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;
  }

  if (error && !currentSprint) {
    return <div className="bg-red-50 text-red-700 p-4 rounded-lg">{error}</div>;
  }

  if (!currentSprint) return null;

  const s = currentSprint;
  const completionPct = s.stats.totalTasks > 0
    ? Math.round((s.stats.completedTasks / s.stats.totalTasks) * 100)
    : 0;

  return (
    <div className="space-y-4">
      {/* Back link (inline mode only) */}
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to sprints
        </button>
      )}

      {/* Sprint header */}
      <div className={`rounded-xl border p-5 ${statusColors[s.status]}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            {editingName ? (
              <div className="flex gap-2">
                <input
                  autoFocus
                  value={nameInput}
                  onChange={e => setNameInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleNameSave(); if (e.key === 'Escape') setEditingName(false); }}
                  className="border border-gray-300 rounded px-2 py-1 text-lg font-semibold bg-white text-gray-900 focus:outline-none"
                />
                <button onClick={handleNameSave} className="text-sm text-blue-600 font-medium">Save</button>
                <button onClick={() => setEditingName(false)} className="text-sm text-gray-500">Cancel</button>
              </div>
            ) : (
              <h1
                className="text-xl font-bold text-gray-900 cursor-pointer hover:opacity-70"
                onClick={() => { setNameInput(s.name); setEditingName(true); }}
                title="Click to edit"
              >
                {s.name}
              </h1>
            )}
            <div className="flex gap-3 mt-1 text-sm">
              <span className="capitalize font-medium">{s.status}</span>
              <span className="text-gray-500">·</span>
              <span className="capitalize text-gray-600">{s.sprintType}</span>
              {s.timeline.startDate && (
                <>
                  <span className="text-gray-500">·</span>
                  <span className="text-gray-600">
                    {new Date(s.timeline.startDate).toLocaleDateString()}
                    {s.timeline.endDate && ` → ${new Date(s.timeline.endDate).toLocaleDateString()}`}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Lifecycle buttons */}
          <div className="flex gap-2 flex-wrap">
            {s.status === 'planned' && (
              <button onClick={() => handleLifecycle('start')} className="bg-green-600 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-green-700">
                Start Sprint
              </button>
            )}
            {s.status === 'active' && (
              <>
                <button onClick={() => handleLifecycle('pause')} className="bg-white border border-yellow-400 text-yellow-700 px-3 py-1.5 rounded text-sm hover:bg-yellow-50">
                  Pause
                </button>
                <button onClick={() => handleLifecycle('complete')} className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-blue-700">
                  Complete Sprint
                </button>
              </>
            )}
            {s.status === 'paused' && (
              <button onClick={() => handleLifecycle('resume')} className="bg-green-600 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-green-700">
                Resume
              </button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        {s.stats.totalTasks > 0 && (
          <div className="mt-4">
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span>{s.stats.completedTasks} of {s.stats.totalTasks} tasks done</span>
              <span>{completionPct}%</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${completionPct}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {(['board', 'details', 'add'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 transition-colors ${
              tab === t
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t === 'add' ? '+ Add Items' : t}
          </button>
        ))}
      </div>

      {/* Board tab */}
      {tab === 'board' && board && (
        <DndContext collisionDetection={closestCorners} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-3 min-w-max">
              {COLUMNS.map(col => (
                <div key={col} className={`w-64 rounded-lg border ${COLUMN_COLORS[col]} flex flex-col`}>
                  <div className="px-3 py-2 border-b border-inherit">
                    <span className="text-sm font-semibold text-gray-700">{COLUMN_LABELS[col]}</span>
                    <span className="ml-2 text-xs text-gray-400">{board[col]?.length ?? 0}</span>
                  </div>
                  <DroppableColumn id={col}>
                    <SortableContext items={(board[col] || []).map((t: any) => t._id)} strategy={verticalListSortingStrategy}>
                      {(board[col] || []).map((task: any) => (
                        <SortableTaskCard
                          key={task._id}
                          task={task}
                          onClick={() => navigate(task._itemType === 'bug' ? `/employee/bugs/${task._id}` : `/employee/tasks/${task._id}`)}
                        />
                      ))}
                    </SortableContext>
                  </DroppableColumn>
                </div>
              ))}
            </div>
          </div>
          <DragOverlay>
            {activeTaskId ? (() => {
              const allTasks = Object.values(board).flat() as any[];
              const task = allTasks.find(t => t._id === activeTaskId);
              if (!task) return null;
              return (
                <div className="bg-white rounded border border-blue-300 p-3 shadow-lg w-64 rotate-2">
                  <p className="text-sm font-medium text-gray-900 line-clamp-2" title={task.title}>{task.title}</p>
                </div>
              );
            })() : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Add Items tab — grouped by parent */}
      {tab === 'add' && (
        <div className="space-y-3">
          <div className="flex gap-2 items-center flex-wrap">
            <input
              type="text"
              value={addSearch}
              onChange={e => setAddSearch(e.target.value)}
              placeholder="Search tasks and bugs…"
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1 min-w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={addType}
              onChange={e => setAddType(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
            >
              <option value="all">All types</option>
              <option value="task">Tasks</option>
              <option value="bug">Bugs</option>
            </select>
          </div>

          {backlogLoading ? (
            <div className="flex justify-center p-8"><div className="animate-spin h-6 w-6 border-4 border-blue-500 border-t-transparent rounded-full" /></div>
          ) : (() => {
            const filtered = backlogItems.filter(item => {
              const matchType = addType === 'all' || item.type === addType;
              const matchSearch = !addSearch || item.title.toLowerCase().includes(addSearch.toLowerCase());
              return matchType && matchSearch;
            });

            if (filtered.length === 0) {
              return (
                <div className="flex flex-col items-center justify-center min-h-[40vh] text-center px-8">
                  <div className="w-16 h-16 rounded-2xl bg-green-50 flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-gray-700">No backlog items available</p>
                  <p className="text-xs text-gray-400 mt-1">All tasks and bugs are either already in a sprint or none exist yet.</p>
                </div>
              );
            }

            // Group items by parent
            const groups: Record<string, { label: string; icon: string; items: BacklogItem[] }> = {};
            for (const item of filtered) {
              const pid = (item as any).parentId;
              const ptype = (item as any).parentType;
              let groupKey = '_unassigned';
              let label = 'Unassigned';
              let icon = 'project';

              if (pid && ptype === 'feature' && parentMap[pid]) {
                groupKey = `feature_${pid}`;
                label = `Feature: ${parentMap[pid].name}`;
                icon = 'feature';
              } else if (pid && ptype === 'folder' && parentMap[pid]) {
                groupKey = `folder_${pid}`;
                label = `Folder: ${parentMap[pid].name}`;
                icon = 'folder';
              } else if (item.type === 'bug') {
                groupKey = '_bugs';
                label = 'Bugs';
                icon = 'bug';
              }

              if (!groups[groupKey]) groups[groupKey] = { label, icon, items: [] };
              groups[groupKey].items.push(item);
            }

            const sortedGroups = Object.entries(groups).sort(([a], [b]) => {
              if (a.startsWith('feature')) return -1;
              if (b.startsWith('feature')) return 1;
              if (a.startsWith('folder')) return -1;
              if (b.startsWith('folder')) return 1;
              return 0;
            });

            const toggleGroup = (key: string) => {
              setExpandedGroups(prev => {
                const next = new Set(prev);
                next.has(key) ? next.delete(key) : next.add(key);
                return next;
              });
            };

            const handleAddGroup = async (items: BacklogItem[]) => {
              for (const item of items) {
                await dispatch(addItemToSprint({ id: sprintId!, itemId: item._id, itemType: item.type }));
              }
              setBacklogItems(prev => prev.filter(i => !items.some(gi => gi._id === i._id)));
              dispatch(fetchSprintBoard({ id: sprintId! }));
            };

            return (
              <div className="space-y-2">
                {sortedGroups.map(([key, group]) => {
                  const isCollapsed = !expandedGroups.has(key);
                  return (
                    <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Group header */}
                      <div
                        className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 cursor-pointer hover:bg-gray-100"
                        onClick={() => toggleGroup(key)}
                      >
                        <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                        {group.icon === 'feature' && (
                          <svg className="w-4 h-4 text-purple-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 01-.657.643 48.491 48.491 0 01-4.163-.3c.186 1.613.293 3.25.315 4.907a.656.656 0 01-.658.663v0c-.355 0-.676-.186-.959-.401a1.647 1.647 0 00-1.003-.349c-1.036 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401v0c.31 0 .555.26.532.57a48.039 48.039 0 01-.642 5.056c1.518.19 3.058.309 4.616.354a.64.64 0 00.657-.643v0c0-.355-.186-.676-.401-.959a1.647 1.647 0 01-.349-1.003c0-1.035 1.008-1.875 2.25-1.875 1.243 0 2.25.84 2.25 1.875 0 .369-.128.713-.349 1.003-.215.283-.4.604-.4.959v0c0 .333.277.599.61.58a48.1 48.1 0 005.427-.63 48.05 48.05 0 00.582-4.717.532.532 0 00-.533-.57v0c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.035 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.37 0 .713.128 1.003.349.283.215.604.401.96.401v0a.656.656 0 00.658-.663 48.422 48.422 0 00-.37-5.36c-1.886.342-3.81.574-5.766.689a.578.578 0 01-.61-.58v0z" />
                          </svg>
                        )}
                        {group.icon === 'folder' && (
                          <svg className="w-4 h-4 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
                          </svg>
                        )}
                        {group.icon === 'bug' && (
                          <svg className="w-4 h-4 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 12.75c1.148 0 2.278.08 3.383.237 1.037.146 1.866.966 1.866 2.013 0 3.728-2.35 6.75-5.25 6.75S6.75 18.728 6.75 15c0-1.046.83-1.867 1.866-2.013A24.204 24.204 0 0112 12.75z" />
                          </svg>
                        )}
                        <span className="text-sm font-medium text-gray-700 flex-1">{group.label}</span>
                        <span className="text-xs text-gray-400">{group.items.length}</span>
                        <button
                          onClick={e => { e.stopPropagation(); handleAddGroup(group.items); }}
                          className="text-xs bg-blue-600 text-white px-2.5 py-1 rounded font-medium hover:bg-blue-700 shrink-0"
                        >
                          Add All
                        </button>
                      </div>

                      {/* Group items */}
                      {!isCollapsed && (
                        <div className="divide-y divide-gray-100">
                          {group.items.map(item => (
                            <div key={item._id} className="flex items-center justify-between gap-3 px-4 py-2 pl-10 hover:bg-gray-50">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full shrink-0 ${
                                  item.type === 'bug' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                                }`}>{item.type}</span>
                                <span className="text-sm text-gray-900 truncate">{item.title}</span>
                                {item.priority && (
                                  <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${
                                    item.priority === 'critical' ? 'bg-red-100 text-red-700' :
                                    item.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                                    item.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>{item.priority}</span>
                                )}
                                {(item as any).storyPoints > 0 && (
                                  <span className="text-xs text-blue-600 font-medium shrink-0">{(item as any).storyPoints} SP</span>
                                )}
                              </div>
                              <button
                                onClick={() => handleAddItem(item)}
                                disabled={addingId === item._id}
                                className="bg-blue-600 text-white px-3 py-1 rounded text-xs font-medium hover:bg-blue-700 disabled:opacity-50 shrink-0"
                              >
                                {addingId === item._id ? 'Adding…' : 'Add'}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* Details tab */}
      {tab === 'details' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          {s.description && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Description</h3>
              <p className="text-sm text-gray-600">{s.description}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Status</span>
              <p className="text-gray-600 capitalize mt-0.5">{s.status}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Type</span>
              <p className="text-gray-600 capitalize mt-0.5">{s.sprintType}</p>
            </div>
            {s.capacity && (
              <div>
                <span className="font-medium text-gray-700">Capacity</span>
                <p className="text-gray-600 mt-0.5">{s.capacity} pts</p>
              </div>
            )}
            {s.velocity !== undefined && (
              <div>
                <span className="font-medium text-gray-700">Velocity</span>
                <p className="text-gray-600 mt-0.5">{s.velocity} pts/day</p>
              </div>
            )}
          </div>

          {/* Sprint Roles / Governance */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700">Sprint Roles</h3>
              {s.sprintType === 'strict' && (
                <button onClick={() => setShowAddRole(!showAddRole)} className="text-xs text-blue-600 hover:text-blue-700">
                  {showAddRole ? 'Cancel' : '+ Add Role'}
                </button>
              )}
            </div>
            {s.roles.length > 0 ? (
              <div className="space-y-1">
                {s.roles.map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-gray-900">{r.userName}</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                        r.role === 'owner' ? 'bg-purple-100 text-purple-700' :
                        r.role === 'editor' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{r.role}</span>
                      {r.role !== 'owner' && (
                        <button onClick={() => handleRemoveRole(r.userId)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400">No roles assigned yet.</p>
            )}
            {showAddRole && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-2">
                <input
                  value={newRoleUserName}
                  onChange={e => setNewRoleUserName(e.target.value)}
                  placeholder="User name"
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <input
                  value={newRoleUserId}
                  onChange={e => setNewRoleUserId(e.target.value)}
                  placeholder="User ID"
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <select value={newRoleType} onChange={e => setNewRoleType(e.target.value as any)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm">
                  <option value="editor">Editor</option>
                  <option value="viewer">Viewer</option>
                </select>
                <button onClick={handleAddRole} className="bg-blue-600 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-blue-700 w-full">Add</button>
              </div>
            )}
          </div>

          {features.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Features in Sprint</h3>
              <div className="space-y-1">
                {features.map((f: any) => (
                  <div key={f._id} className="text-sm text-gray-700 border-l-2 border-blue-300 pl-3">
                    {f.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          <AmendmentLog amendments={s.amendmentLog ?? []} />
          <BreachHistory breaches={s.breachHistory ?? []} />
        </div>
      )}

      {/* Incomplete Items Resolution Modal */}
      {showIncompleteModal && sprintId && incompleteItems.length > 0 && (
        <IncompleteItemsModal
          sprintId={sprintId}
          items={incompleteItems}
          onClose={() => {
            setShowIncompleteModal(false);
            dispatch(fetchSprintBoard({ id: sprintId }));
          }}
        />
      )}
    </div>
  );
};

export default SprintDetail;
